import * as React from 'react';

import { IBaseQuestionProps } from '../FormContainer/types/internal';
import { RealtorsListingLayout } from '../../components/RealtorsListingLayout';
import { ModalLoader } from '../../components/ModalLoader';
import { ErrorBlock } from '../../components/ErrorBlock';
import { useRealtorListing } from './utils/useRealtorListing';
import { PaginationContainer } from '../PaginationContainer';
import { useRealtorPagination } from './utils/useRealtorPagination';
import { CounterBannerRealtorsContainer } from '../CounterBannerRealtorsContainer';
import { ListOfRealtorsContainer } from '../ListOfRealtorsContainer';
import { useRealtorCardData } from './useRealtorCardData';
import { AboutRealtorModalContainer } from '../AboutRealtorModalContainer';
import { useScroll } from './utils/useScroll';
import { SpecificWrapper } from '../../components/SpecificWrapper/SpecificWrapper';
import { getGeoMapper } from '../../utils/getGeoMapper';
import { EDealTypeEx } from '../../repositories/demand/v4/create-message';
import { MAX_REALTORS } from './utils/constants';

export const RealtorsListingContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { answerField },
    setNextStep,
    setAnswersState,
    answersState,
  } = props;
  const {
    realtor,
    resetRealtorCardData,
    setRealtorCardData,
    realtorCardData,
    isRealtorCardDataFetching,
    isRealtorCardDataError,
  } = useRealtorCardData();

  const geoMapper = getGeoMapper(answersState.dealTypeEx?.value as EDealTypeEx);
  const geoObj = answersState.address && geoMapper(answersState.address);
  const {
    selectedRealtorsId,
    specialistsList,
    fetchSpecialists,
    isFetching,
    handleSelectRealtor,
    handleUnselectRealtor,
    handleSubmit,
  } = useRealtorListing({
    answerField,
    setNextStep,
    setAnswersState,
    geo: geoObj?.geo,
  });

  const { page, handleOnChangePage } = useRealtorPagination(geoObj?.geo);
  const { scrolledElementRef, scrollToTop } = useScroll();

  const isRealtorsLimitReached = selectedRealtorsId.length === MAX_REALTORS;
  const isSelectedRealtor = realtor?.id ? selectedRealtorsId.includes(realtor.id) : false;
  const isButtonDisabled =
    (isRealtorsLimitReached && !isSelectedRealtor) || isRealtorCardDataError || isRealtorCardDataFetching;

  if (isFetching) {
    return (
      <SpecificWrapper>
        <ModalLoader />
      </SpecificWrapper>
    );
  }

  if (!specialistsList) {
    return (
      <SpecificWrapper>
        <ErrorBlock
          title="Произошла ошибка"
          text={'Что-то пошло не так. \n Попробуйте еще раз'}
          buttonText="Повторить"
          onClick={fetchSpecialists}
        />
      </SpecificWrapper>
    );
  }

  return (
    <>
      <RealtorsListingLayout
        banner={<CounterBannerRealtorsContainer onClick={handleSubmit} countSelected={selectedRealtorsId.length} />}
        list={
          <ListOfRealtorsContainer
            realtors={specialistsList}
            selectedRealtorsId={selectedRealtorsId}
            selectRealtor={handleSelectRealtor}
            unselectRealtor={handleUnselectRealtor}
            onRealtorClick={setRealtorCardData}
            isButtonDisabled={isButtonDisabled}
          />
        }
        pagination={<PaginationContainer scrollToTop={scrollToTop} pagination={page} action={handleOnChangePage} />}
        scrolledElementRef={scrolledElementRef}
      />
      <AboutRealtorModalContainer
        realtor={realtor}
        realtorCardData={realtorCardData}
        resetRealtorCardData={resetRealtorCardData}
        handleSelectRealtor={handleSelectRealtor}
        handleUnselectRealtor={handleUnselectRealtor}
        isRealtorCardDataError={isRealtorCardDataError}
        isRealtorCardDataFetching={isRealtorCardDataFetching}
        isSelectedRealtor={isSelectedRealtor}
        isButtonDisabled={isButtonDisabled}
      />
    </>
  );
};

RealtorsListingContainer.displayName = 'RealtorsListingContainer';
